import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Popover } from '@svg/antd';
import useStyles from './style';
import EmptyState, { type TopicEntry } from './components/EmptyState';
import SearchState from './components/SearchState';

/**
 * 搜索下拉面板组件的属性接口
 * 定义了搜索弹出面板的所有配置选项和回调函数
 */
interface SearchPopoverProps {
  /** 控制弹出面板的显示/隐藏状态 */
  visible: boolean;
  /** 弹出面板显示状态变化的回调函数 */
  onVisibleChange: (visible: boolean) => void;
  /** 当前搜索输入框的值 */
  searchValue: string;
  /** 选择搜索项时的回调函数，包含搜索值、选中项和类型信息 */
  onSelect?: (searchValue: string, select: any, type: string) => void;
  /** 触发弹出面板的子元素，必须包含 ref 属性 */
  children: React.ReactElement & { ref?: React.Ref<any> };
  /** 搜索历史记录列表 */
  searchHistory?: string[];
  /** 热门搜索关键词列表 */
  hotSearch?: string[];
  /** 空搜索状态下展示的专题入口列表 */
  topicEntries?: TopicEntry[];
  /** 搜索分类数据，包含分类的键、值和高亮文本 */
  searchCategorie?: Array<{ key: number; value: string; hightLight: string }>;
  /** 推荐搜索关键词列表 */
  recommendation?: string[];
  /** 分类推荐数据，包含分类的键和路径值数组 */
  categoriesRecommend?: Array<{ key: number; value: string[] }>;
  /** 清空搜索历史记录的回调函数 */
  onClearHistory?: () => void;
  /** 弹出面板的尺寸大小，可选值为 'large' 或 'normal' */
  size?: 'large' | 'normal';
}

const AFFIX_WIDTH_SELECTOR = '.ant-input-affix-wrapper, .ant-input-group-wrapper';
const WIDTH_STABILITY_DELAY = 160;

const useChildrenWidth = (children: React.ReactElement & { ref?: React.Ref<any> }) => {
  const childrenRef = useRef<HTMLDivElement | null>(null);
  const [childrenWidth, setChildrenWidth] = useState<number>(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const affixElementRef = useRef<HTMLElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const getAffixElement = () => {
      if (!childrenRef.current) {
        return null;
      }

      return childrenRef.current.querySelector<HTMLElement>(AFFIX_WIDTH_SELECTOR);
    };

    const syncAffixObserver = () => {
      const nextAffixElement = getAffixElement();

      if (affixElementRef.current === nextAffixElement) {
        return nextAffixElement;
      }

      if (resizeObserverRef.current && affixElementRef.current) {
        resizeObserverRef.current.unobserve(affixElementRef.current);
      }

      affixElementRef.current = nextAffixElement;

      if (resizeObserverRef.current && affixElementRef.current) {
        resizeObserverRef.current.observe(affixElementRef.current);
      }

      return affixElementRef.current;
    };

    const updateWidth = () => {
      if (childrenRef.current && childrenRef.current instanceof Element) {
        const affixElement = syncAffixObserver();
        const containerWidth = childrenRef.current.offsetWidth;
        const affixWidth = affixElement?.offsetWidth || 0;
        setChildrenWidth(Math.max(containerWidth, affixWidth));
      }
    };

    // 使用 requestAnimationFrame 来平滑更新宽度，避免突然跳跃
    const updateWidthWithRAF = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        updateWidth();
      });
    };

    // 防抖函数，用于 ResizeObserver 回调
    const debouncedUpdateWidth = debounce(updateWidthWithRAF, 50);

    // 立即获取初始宽度，然后延迟再次更新以确保准确
    updateWidth();
    const timeoutId = setTimeout(() => {
      updateWidth();
    }, 100);

    // 使用 ResizeObserver 监听元素尺寸变化，性能更好
    const setupResizeObserver = () => {
      if (childrenRef.current && childrenRef.current instanceof Element && typeof ResizeObserver !== 'undefined') {
        try {
          resizeObserverRef.current = new ResizeObserver(() => {
            debouncedUpdateWidth();
          });
          resizeObserverRef.current.observe(childrenRef.current);
          syncAffixObserver();
          return true;
        } catch (error) {
          console.warn('ResizeObserver error:', error);
          return false;
        }
      }
      return false;
    };

    if (!setupResizeObserver()) {
      // 降级方案：监听窗口变化，使用防抖优化性能
      const handleResize = debounce(updateWidthWithRAF, 50);
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timeoutId);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        affixElementRef.current = null;
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => {
      clearTimeout(timeoutId);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      affixElementRef.current = null;
    };
  }, [children]);

  return { childrenRef, childrenWidth };
};

// 防抖函数，优化性能
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const useDelayedContent = (
  searchValue: string,
  onSelect?: (searchValue: string, select: any, type: string) => void,
  searchHistory?: string[],
  hotSearch?: string[],
  topicEntries?: TopicEntry[],
  searchCategorie?: Array<{ key: number; value: string; hightLight: string }>,
  recommendation?: string[],
  categoriesRecommend?: { key: number; value: string[]; }[],
  onClearHistory?: () => void,
  size?: 'large' | 'normal'
) => {
  const [displayContent, setDisplayContent] = useState<'empty' | 'search'>(
    searchValue ? 'search' : 'empty'
  );
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (searchValue && displayContent === 'empty') {
      // 从EmptyState切换到SearchState，添加延迟
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayContent('search');
        setIsTransitioning(false);
      }, 200);
      return () => clearTimeout(timer);
    } else if (!searchValue && displayContent === 'search') {
      // 从SearchState切换到EmptyState，添加延迟
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayContent('empty');
        setIsTransitioning(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [searchValue, displayContent]);

  if (isTransitioning) {
    // 在过渡期间显示加载状态或保持当前内容
    return displayContent === 'search' ?
      <SearchState
        searchValue={searchValue}
        onSelect={onSelect}
        searchCategorie={searchCategorie}
        recommendation={recommendation}
        categoriesRecommend={categoriesRecommend}
      /> :
      <EmptyState
        onSelect={onSelect}
        searchHistory={searchHistory}
        hotSearch={hotSearch}
        topicEntries={topicEntries}
        onClearHistory={onClearHistory}
        size={size}
      />;
  }

  return displayContent === 'search' ?
    <SearchState
      searchValue={searchValue}
      onSelect={onSelect}
      searchCategorie={searchCategorie}
      recommendation={recommendation}
      categoriesRecommend={categoriesRecommend}
    /> :
    <EmptyState
      onSelect={onSelect}
      searchHistory={searchHistory}
      hotSearch={hotSearch}
      topicEntries={topicEntries}
      onClearHistory={onClearHistory}
      size={size}
    />;
};

interface RenderPopoverContentProps {
  searchValue: string;
  onSelect?: (searchValue: string, select: any, type: string) => void;
  searchHistory?: string[];
  hotSearch?: string[];
  topicEntries?: TopicEntry[];
  searchCategorie?: Array<{ key: number; value: string; hightLight: string }>;
  recommendation?: string[];
  categoriesRecommend?: { key: number; value: string[]; }[];
  onClearHistory?: () => void;
  size?: 'large' | 'normal'
}

const renderPopoverContent = ({
  searchValue,
  onSelect,
  searchHistory,
  hotSearch,
  topicEntries,
  searchCategorie,
  recommendation,
  categoriesRecommend,
  onClearHistory,
  size
}: RenderPopoverContentProps) => {
  return useDelayedContent(
    searchValue,
    onSelect,
    searchHistory,
    hotSearch,
    topicEntries,
    searchCategorie,
    recommendation,
    categoriesRecommend,
    onClearHistory,
    size
  );
};

const renderChildrenWithRef = (
  children: React.ReactElement & { ref?: React.Ref<any> },
  childrenRef: React.RefObject<HTMLDivElement>
) => {
  // 简化处理，直接返回子元素，因为外部的 div 已经处理了 ref
  return children;
};

const SearchPopover: React.FC<SearchPopoverProps> = ({
  visible,
  onVisibleChange,
  searchValue,
  onSelect,
  children,
  searchHistory,
  hotSearch,
  topicEntries = [],
  searchCategorie,
  recommendation,
  categoriesRecommend,
  onClearHistory,
  size = 'normal',
}) => {
  const { styles } = useStyles();
  const { childrenRef, childrenWidth } = useChildrenWidth(children);
  const [delayedVisible, setDelayedVisible] = useState(false);
  const lastWidthRef = useRef<number>(0);
  const stabilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownRef = useRef<boolean>(false);

  // 检测宽度是否稳定，稳定后立即显示（只显示一次）
  useEffect(() => {
    if (!visible) {
      // 如果不可见，立即隐藏并重置状态（不延迟）
      setDelayedVisible(false);
      lastWidthRef.current = 0;
      hasShownRef.current = false;
      if (stabilityTimerRef.current) {
        clearTimeout(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }
      return;
    }

    // 如果已经显示过，保持显示状态
    if (hasShownRef.current) {
      setDelayedVisible(true);
      return;
    }

    // 如果可见且宽度已计算
    if (childrenWidth > 0) {
      // 如果宽度发生变化，重置计时器
      if (lastWidthRef.current !== childrenWidth) {
        if (stabilityTimerRef.current) {
          clearTimeout(stabilityTimerRef.current);
        }
        lastWidthRef.current = childrenWidth;
        setDelayedVisible(false);

        // 等待初始宽度复测和输入框宽度动画稳定，避免下拉内容先换行再回到一行。
        stabilityTimerRef.current = setTimeout(() => {
          // 再次检查宽度是否没有变化
          if (lastWidthRef.current === childrenWidth && visible && !hasShownRef.current) {
            setDelayedVisible(true);
            hasShownRef.current = true;
          }
          stabilityTimerRef.current = null;
        }, WIDTH_STABILITY_DELAY);
      } else if (lastWidthRef.current === childrenWidth && lastWidthRef.current > 0) {
        // 如果宽度已经稳定且还没显示过，立即显示
        if (!hasShownRef.current) {
          setDelayedVisible(true);
          hasShownRef.current = true;
          if (stabilityTimerRef.current) {
            clearTimeout(stabilityTimerRef.current);
            stabilityTimerRef.current = null;
          }
        }
      }
    }

    return () => {
      if (stabilityTimerRef.current) {
        clearTimeout(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }
    };
  }, [visible, childrenWidth]);

  const handleOpenChange = (open: boolean, event?: any) => {
    if (event) {
      onVisibleChange(open);
    }
  };

  // 使用 useMemo 确保 shouldHidePopover 是响应式的，当相关 props 变化时重新计算
  const shouldHidePopover = useMemo(() => {
    // 当 searchValue 不存在时
    if (!searchValue) {
      // 如果 searchHistory 或 hotSearch 存在，则显示 Popover（返回 false）
      // 如果都不存在，则隐藏 Popover（返回 true）
      return (!searchHistory || searchHistory.length === 0) &&
        (!hotSearch || hotSearch.length === 0) &&
        (!topicEntries || topicEntries.length === 0);
    }

    // 当 searchValue 存在时
    // 如果 searchCategorie 或 recommendation 或 categoriesRecommend 存在，则显示 Popover（返回 false）
    // 如果都不存在，则隐藏 Popover（返回 true）
    return (!searchCategorie || searchCategorie.length === 0) &&
      (!recommendation || recommendation.length === 0) &&
      (!categoriesRecommend || categoriesRecommend.length === 0);
  }, [searchValue, searchHistory, hotSearch, topicEntries, searchCategorie, recommendation, categoriesRecommend]);

  const popoverWidth = childrenWidth || 'auto';
  const popoverWidthStyle = useMemo(() => ({ width: popoverWidth }), [popoverWidth]);
  const popoverBodyStyle = useMemo(
    () => ({
      width: popoverWidth,
      transition: 'none'
    }),
    [popoverWidth]
  );
  const popoverWidthProps: Record<string, any> = useMemo(
    () => ({
      styles: {
        root: popoverWidthStyle,
        body: popoverBodyStyle
      },
      overlayStyle: popoverWidthStyle,
      overlayInnerStyle: popoverWidthStyle
    }),
    [popoverWidthStyle, popoverBodyStyle]
  );

  return (
    <Popover
      open={visible && !shouldHidePopover && delayedVisible}
      onOpenChange={handleOpenChange}
      arrow={false}
      placement="bottomLeft"
      rootClassName={`${styles.popover}${size === 'normal' ? ' mini-popover' : ' large-popover'}${shouldHidePopover ? ' hidden-popover' : ''}`}
      content={renderPopoverContent({
        searchValue,
        onSelect,
        searchHistory,
        hotSearch,
        topicEntries,
        searchCategorie,
        recommendation,
        categoriesRecommend,
        onClearHistory,
        size
      })}
      destroyTooltipOnHide
      {...popoverWidthProps}
      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
    >
      <div ref={childrenRef} style={{ width: '100%' }}>
        {renderChildrenWithRef(children, childrenRef)}
      </div>
    </Popover>
  );
};

// 导出主要组件和工具函数，便于测试和复用
export { useChildrenWidth, renderPopoverContent, renderChildrenWithRef };
export default SearchPopover;
